import { useId } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  ProviderNpiSelect,
  type ProviderNpiValue,
} from "@/components/Selects/ProviderNpiSelect";
import { NPI_PATTERN } from "@/features/credentials/components/license-verification/types";

export function NpiSelection({
  providerName,
  savedNpi,
  hasSavedLicenses,
  selected,
  onChange,
  onConfirm,
  pending,
  lookingUp,
  warning,
}: {
  providerName: string;
  savedNpi: string | null;
  hasSavedLicenses: boolean;
  selected: ProviderNpiValue | null;
  onChange: (provider: ProviderNpiValue | null) => void;
  onConfirm: () => void;
  pending: boolean;
  lookingUp: boolean;
  warning: string | null;
}) {
  const searchId = useId();
  const hasSavedNpi = NPI_PATTERN.test(savedNpi ?? "");
  const selectedName = selected
    ? [
        selected.firstName,
        selected.middleName,
        selected.lastName,
        selected.suffixName,
      ]
        .filter(Boolean)
        .join(" ")
    : "";
  let confirmLabel = "Confirm NPI and fill licenses";
  if (hasSavedNpi) {
    confirmLabel = "Load state licenses";
  }
  if (lookingUp) {
    confirmLabel = "Loading state licenses…";
  }
  return (
    <section className="space-y-3" aria-label="Provider identity">
      <div className="space-y-1">
        <h3 className="font-medium">Provider identity</h3>
        <p className="text-sm text-muted-foreground">{providerName}</p>
      </div>
      {hasSavedNpi ? (
        <div className="rounded-xl border bg-slate-50 p-3 text-sm">
          <span className="text-muted-foreground">Saved NPI</span>
          <p className="font-medium">{savedNpi}</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            Select this provider’s NPI record. Compare the name, specialty, and
            location before confirming.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor={searchId}>Search by name or NPI number</Label>
            <ProviderNpiSelect
              inputId={searchId}
              value={selected ?? undefined}
              onChange={onChange}
              disabled={pending}
              placeholder="Enter a full name or 10-digit NPI"
              searchErrorMessage="Could not search the NPI registry. Try the search again before confirming."
            />
          </div>
          {selected && (
            <div className="rounded-xl border bg-slate-50 p-3 text-sm">
              <p className="font-medium">{selectedName}</p>
              <p>NPI {selected.npi}</p>
              {selected.specialties.some((specialty) => specialty.desc) && (
                <p className="text-xs text-muted-foreground">
                  {selected.specialties
                    .map((specialty) => specialty.desc)
                    .filter(Boolean)
                    .join(", ")}
                </p>
              )}
            </div>
          )}
        </>
      )}
      {(!hasSavedNpi || !hasSavedLicenses) && (
        <div className="space-y-2">
          <Button
            type="button"
            variant={hasSavedNpi ? "outline" : "default"}
            disabled={pending || (!hasSavedNpi && !selected)}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
          <p className="text-xs text-muted-foreground">
            Clicking this button{" "}
            {hasSavedNpi ? "looks up" : "saves the NPI and looks up"} state
            license details. You can review and edit the results below.
          </p>
        </div>
      )}
      {warning && (
        <p role="status" className="text-sm text-amber-900">
          {warning}
        </p>
      )}
    </section>
  );
}
